<template>
  <teleport to="#modals">
    <vue-resizable
      v-slot="{ style }"
      class="z-[100]"
      drag-selector=".cursor-grab"
      v-bind="activeModalLocalStorageOptions"
      :min-width="activeModalWidthAndHeight.minWidth"
      :min-height="activeModalWidthAndHeight.minHeight"
      :max-width="activeModalWidthAndHeight?.maxWidth"
      :max-height="activeModalWidthAndHeight?.maxHeight"
      @mount="updatePosition"
      @resize:end="updatePosition"
      @drag:end="updatePosition"
    >
      <div
        ref="resizableElement"
        :style="style"
        class="rounded-md shadow-lg"
        :class="[
          isMinimized ? 'max-w-55' : 'h-full',
          isDealsView && isAllActivity ? 'bg-disabled' : 'bg-white',
          modalClass,
        ]"
        data-cy="communication-dialog"
      >
        <div
          class="flex items-center justify-between space-x-2 rounded-t-md bg-grey-navigation p-4 text-white"
          :class="{ 'max-w-55 rounded-b-md': isMinimized }"
          data-cy="communication-dialog-header"
        >
          <div
            class="flex min-w-0 grow cursor-grab items-center space-x-4"
            data-cy="handle"
          >
            <icon-base
              :icon="IconCardView"
              width="16"
              height="16"
              view-box="0 0 24 24"
              class="handle min-w-4 rotate-90 transform"
            />
            <span
              class="grow overflow-hidden leading-4 font-semibold text-ellipsis whitespace-nowrap text-white"
              @dblclick="toggleMinimize"
            >
              {{ modalTitle }}
            </span>
          </div>
          <div class="flex space-x-2">
            <icon-base
              v-if="isMinimized"
              :icon="IconWidget"
              view-box="0 0 16 14"
              icon-name="Restore"
              :title="$t('COMMON.RESTORE')"
              class="cursor-pointer text-white"
              data-cy="maximize-communication-dialog"
              @click="toggleMinimize"
            />
            <icon-base
              v-else
              :icon="IconSubtract"
              view-box="0 -3 16 16"
              icon-name="Minimize"
              :title="$t('COMMON.MINIMIZE')"
              class="cursor-pointer text-white"
              data-cy="minimize-communication-dialog"
              @click="toggleMinimize"
            />
            <icon-base
              :icon="IconClose"
              icon-name="Close"
              view-box="0 0 18 18"
              data-cy="close-communication-dialog"
              class="cursor-pointer text-white"
              @click="emit('close-dialog')"
            />
          </div>
        </div>
        <loader :is-loading="loading" />
        <upload-call-recording
          v-if="
            uploadCallRecordingOptions.enabled &&
            uploadCallRecordingOptions.note
          "
          :call-log="uploadCallRecordingOptions.note"
          @call-note:uploaded="finishCallNoteUploaded"
        />
        <slot name="content">
          <!-- to prevent modal data from not being populated-->
          <component
            :is="modalTypeMap[modalType].component"
            v-if="!loading && !uploadCallRecordingOptions.enabled"
            v-show="!isMinimized"
            :class="modalTypeMap[modalType].componentClass"
            is-modal
            :standalone
            :phone-number="phoneNumber"
            :application-id="dealId"
            :reply-to-message="replyToMessage"
            :selected-email="selectedEmail"
            @close-dialog="emit('close-dialog')"
            @call-note:create="handleCallNoteCreate"
          />
        </slot>
      </div>
    </vue-resizable>
  </teleport>
</template>

<script setup lang="ts">
import { computed, onMounted, ref } from "vue";
import { useI18n } from "vue-i18n";
import VueResizable from "vue-resizable";
import { useRoute } from "vue-router";
import { useStore } from "vuex";

import isEmpty from "lodash/isEmpty";

import { CommunicationType } from "@/enums/communicationLogs";
import { convertToArray } from "@/helpers/common";
import { allActivityType } from "@/helpers/communication";
import {
  DEFAULT_MINIMIZED_HEIGHT,
  DEFAULT_MINIMIZED_WIDTH,
} from "@/helpers/constants";
import {
  DEFAULT_ALL_ACTIVITY_MODAL_OPTIONS,
  DEFAULT_CALL_MODAL_OPTIONS,
  DEFAULT_EMAIL_MODAL_OPTIONS,
  DEFAULT_SMS_MODAL_OPTIONS,
  DEFAULT_TASK_MODAL_OPTIONS,
} from "@/helpers/constants/deals";
import { usePromiseWrapper } from "@/hooks/common";
import { useLocalStorageSetting } from "@/hooks/options";
import { useEmailModalDataQuery } from "@/hooks/queries/useInbox";
import { useResizable } from "@/hooks/resizable";
import { ROUTE_DEALS } from "@/router/routes";
import useEmailsStore from "@/stores/emails";
import useSmsStore from "@/stores/sms";

import SmsForm from "@/components/communicationLogs/communication/sms/SmsForm.vue";
import CreateCallNoteForm from "@/components/communicationLogs/forms/CreateCallNoteForm.vue";
import TaskForm from "@/components/communicationLogs/forms/CreateTaskForm.vue";
import UploadCallRecording from "@/components/communicationLogs/forms/UploadCallRecording.vue";
import EmailForm from "@/components/emailSending/EmailForm.vue";
import IconCardView from "@/components/icons/IconCardView.vue";
import IconClose from "@/components/icons/IconClose.vue";
import IconSubtract from "@/components/icons/IconSubtract.vue";
import IconWidget from "@/components/icons/IconWidget.vue";

import type {
  CallLog,
  CommunicationModalType,
} from "@/models/communicationLogs";
import type { EmailModalDataRequestPayload } from "@/models/emails";
import type { DynamicFieldOptions } from "@/models/options";

type CallRecordingOptions = {
  enabled: boolean;
  note: CallLog | null;
};

const emit = defineEmits<{
  "close-dialog": [];
}>();

const {
  dealId = "",
  dealName = "",
  selectedEmail = "",
  type = CommunicationType.email,
  title = "",
  replyToMessage = "",
  standalone = false,
  phoneNumber = "",
  emailThreadIds,
} = defineProps<{
  dealId?: string | string[];
  dealName?: string | undefined;
  type?: CommunicationModalType | typeof allActivityType;
  title?: string;
  replyToMessage?: string;
  selectedEmail?: string;
  standalone?: boolean;
  phoneNumber?: string;
  emailThreadIds?: number[];
}>();

const { getters, dispatch } = useStore();
const emailStore = useEmailsStore();
const smsStore = useSmsStore();
const route = useRoute();
const { t } = useI18n();

const emailModalDataPayload = ref<EmailModalDataRequestPayload | null>(null);
const { refetch: refetchEmailModalData } = useEmailModalDataQuery(
  emailModalDataPayload
);

const emailOptions = useLocalStorageSetting(
  "emailModalOptions",
  DEFAULT_EMAIL_MODAL_OPTIONS
);

const smsOptions = useLocalStorageSetting(
  "smsModalOptions",
  DEFAULT_SMS_MODAL_OPTIONS
);

const taskOptions = useLocalStorageSetting(
  "taskModalOptions",
  DEFAULT_TASK_MODAL_OPTIONS
);

const callOptions = useLocalStorageSetting(
  "callModalOptions",
  DEFAULT_CALL_MODAL_OPTIONS
);

const allActivityOptions = useLocalStorageSetting(
  "allActivityModalOptions",
  DEFAULT_ALL_ACTIVITY_MODAL_OPTIONS
);

const uploadCallRecordingOptions = ref<CallRecordingOptions>({
  enabled: false,
  note: null,
});

const emailWidthAndHeight = ref({
  width: DEFAULT_EMAIL_MODAL_OPTIONS.width,
  height: DEFAULT_EMAIL_MODAL_OPTIONS.height,
  minWidth: 500,
  maxWidth: undefined,
  minHeight: 400,
  maxHeight: undefined,
  minimizedWidth: DEFAULT_MINIMIZED_WIDTH,
  minimizedHeight: DEFAULT_MINIMIZED_HEIGHT,
});

const smsWidthAndHeight = ref({
  width: DEFAULT_SMS_MODAL_OPTIONS.width,
  height: DEFAULT_SMS_MODAL_OPTIONS.height,
  minWidth: 550,
  maxWidth: undefined,
  minHeight: 400,
  maxHeight: undefined,
  minimizedWidth: DEFAULT_MINIMIZED_WIDTH,
  minimizedHeight: DEFAULT_MINIMIZED_HEIGHT,
});

const taskWidthAndHeight = ref({
  width: DEFAULT_TASK_MODAL_OPTIONS.width,
  height: DEFAULT_TASK_MODAL_OPTIONS.height,
  minWidth: 260,
  maxWidth: 400,
  minHeight: 410,
  maxHeight: 410,
  minimizedWidth: DEFAULT_MINIMIZED_WIDTH,
  minimizedHeight: DEFAULT_MINIMIZED_HEIGHT,
});

const callWidthAndHeight = ref({
  width: 500,
  height: 300,
  minWidth: 500,
  maxWidth: undefined,
  minHeight: 300,
  maxHeight: undefined,
  minimizedWidth: DEFAULT_MINIMIZED_WIDTH,
  minimizedHeight: DEFAULT_MINIMIZED_HEIGHT,
});

const allActivityWidthAndHeight = ref({
  width: DEFAULT_ALL_ACTIVITY_MODAL_OPTIONS.width,
  height: DEFAULT_ALL_ACTIVITY_MODAL_OPTIONS.height,
  minWidth: 500,
  maxWidth: undefined,
  minHeight: 300,
  maxHeight: undefined,
  minimizedWidth: DEFAULT_MINIMIZED_WIDTH,
  minimizedHeight: DEFAULT_MINIMIZED_HEIGHT,
});

const modalTypeMap = {
  [CommunicationType.email]: {
    name: t("COMMON.EMAIL"),
    store: emailStore,
    options: emailOptions,
    widthAndHeight: emailWidthAndHeight,
    component: EmailForm,
    componentClass: "h-[calc(100%-48px)]",
  },
  [CommunicationType.sms]: {
    name: t("COMMON.SMS"),
    store: smsStore,
    options: smsOptions,
    widthAndHeight: smsWidthAndHeight,
    component: SmsForm,
    componentClass: undefined,
  },
  [CommunicationType.task]: {
    name: t("COMMON.TASK"),
    store: undefined,
    options: taskOptions,
    widthAndHeight: taskWidthAndHeight,
    component: TaskForm,
    componentClass: undefined,
  },
  [CommunicationType.call]: {
    name: t("COMMON.CALL"),
    store: undefined,
    options: callOptions,
    widthAndHeight: callWidthAndHeight,
    component: CreateCallNoteForm,
    componentClass: `h-[calc(100%-${DEFAULT_MINIMIZED_HEIGHT}px)]`,
  },
  [allActivityType]: {
    name: t("COMMON.ALL"),
    store: undefined,
    options: allActivityOptions,
    widthAndHeight: allActivityWidthAndHeight,
    component: undefined,
    componentClass: undefined,
  },
};

const isMinimized = ref(false);
const resizableElement = ref<HTMLElement | null>(null);

const dynamicFieldsOptions = computed<DynamicFieldOptions | null>(
  () => getters["options/dynamicFields"]
);

const isEmailModal = computed(() => type === CommunicationType.email);
const modalType = computed(() => type);

const { updatePosition, repositionAsNeeded } = useResizable(
  modalTypeMap[modalType.value].options,
  resizableElement
);

const isDealsView = computed(() => route.name === ROUTE_DEALS);

const isAllActivity = computed(() => modalType.value === allActivityType);

const activeModalWidthAndHeight = computed(
  () => modalTypeMap[modalType.value].widthAndHeight.value
);

const activeModalLocalStorageOptions = computed(
  () => modalTypeMap[modalType.value].options.value
);

const modalTitle = computed(
  () =>
    title ||
    `${modalTypeMap[modalType.value].name}${dealName ? ` - ${dealName}` : ""}`
);

const modalClass = computed(() => (route.params.id ? "deal-modal" : ""));

const toggleMinimize = () => {
  if (!isMinimized.value) {
    const { width, height } = activeModalLocalStorageOptions.value;
    activeModalLocalStorageOptions.value.restoreHeight = height;
    activeModalLocalStorageOptions.value.restoreWidth = width;
  }
  isMinimized.value = !isMinimized.value;

  const { width, height, minimizedWidth, minimizedHeight } =
    activeModalWidthAndHeight.value;

  const { restoreWidth, restoreHeight, left, top } =
    activeModalLocalStorageOptions.value;

  const setWidth = isMinimized.value ? minimizedWidth : restoreWidth || width;
  const setHeight = isMinimized.value
    ? minimizedHeight
    : restoreHeight || height;

  updatePosition({
    left,
    top,
    width: setWidth,
    height: setHeight,
  });
};

const { fetchWrapper: getCommunicationInfo, loading } = usePromiseWrapper(
  async () => {
    if (
      modalType.value !== allActivityType &&
      [CommunicationType.email, CommunicationType.sms].includes(modalType.value)
    ) {
      if (isEmpty(dynamicFieldsOptions.value)) {
        await dispatch("options/getDynamicFields");
      }

      const dealIds = convertToArray(dealId);

      if (isEmailModal.value) {
        const sendAsBulk = dealIds.length > 1;
        const validDealIds = dealIds.filter(Boolean);

        emailModalDataPayload.value = validDealIds.length
          ? { application_ids: validDealIds, send_as_bulk: sendAsBulk }
          : { email_thread_ids: emailThreadIds! };

        await refetchEmailModalData();
      } else {
        if (!standalone) {
          await smsStore.getApplicationSmsModalData(dealIds);
        }
      }
    }

    await repositionAsNeeded();
  }
);

const handleCallNoteCreate = (upload: boolean, note: CallLog) => {
  if (upload) {
    uploadCallRecordingOptions.value.enabled = true;
    uploadCallRecordingOptions.value.note = note;
    return;
  }
  emit("close-dialog");
};

const finishCallNoteUploaded = () => {
  uploadCallRecordingOptions.value.enabled = false;
  uploadCallRecordingOptions.value.note = null;
  emit("close-dialog");
};

onMounted(getCommunicationInfo);
</script>
